import { useCallback, useEffect, useState } from 'react';
import { familyMaturityApi } from '@/services/family-maturity-api';
import type { IFamilyMaturity, MaturityStatus } from '@skystone/shared';

interface UseFamilyMaturityResult {
  rows: IFamilyMaturity[];
  isLoading: boolean;
  error: string | null;
  /** Update a single row in-place; returns the updated row on success. */
  updateStatus: (familyCode: string, status: MaturityStatus) => Promise<IFamilyMaturity>;
  /** Force a refetch (rare — used after bulk admin actions). */
  refetch: () => void;
}

/**
 * Read hook for the family-maturity registry (PRODOPSAI-391 / US5).
 *
 * Loads on mount; exposes an `updateStatus` mutation that optimistically
 * patches the local list when the API call succeeds. On failure the local
 * list is unchanged and the error message is returned to the caller (the
 * UI can decide whether to show inline or toast).
 */
export function useFamilyMaturity(): UseFamilyMaturityResult {
  const [rows, setRows] = useState<IFamilyMaturity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    familyMaturityApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setRows(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load family maturity');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const updateStatus = useCallback(
    async (familyCode: string, status: MaturityStatus): Promise<IFamilyMaturity> => {
      const updated = await familyMaturityApi.update(familyCode, status);
      setRows((prev) =>
        prev.map((row) => (row.familyCode === familyCode ? updated : row)),
      );
      return updated;
    },
    [],
  );

  const refetch = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  return { rows, isLoading, error, updateStatus, refetch };
}
